import { DashStyleValue } from 'highcharts';

import {
  DropoffBreakdown,
  FeedbackBreakdown,
  QuickFeedbackRecord,
  RelevanceBreakdown,
  RelevanceRating,
} from '../apiInterfaces';
import { EventProperty } from '../custom-events/types';
import { EventChartProperty } from '../sharedInsightTypes';
import {
  GraphDataType,
  InsightAgentsTicketsSortColumns,
  InsightAgentTicketsSortColumns,
  MetadataFilterItem,
} from 'src/components/dashboard-pages/agent-insights/types';
import {
  ChatTrendsPeriodicalFilter,
  InsightPeriod,
  InsightSortDirection,
  OverviewOvertimePeriod,
  ProductMode,
} from 'src/components/dashboard-pages/solve-insights/types';
import { InsightWorkflowSortColumns } from 'src/components/dashboard-pages/solve-insights/workflows/constants';
import { ContextVariableFilterItemValue } from 'src/components/global-filter-drawer/types';
import { TranscriptComponent } from 'src/pages/intent-conversation-analytics/ConversationTranscript/types';
import { ClusterTier } from 'src/reducers/discoverReducer/types';
import {
  EndConditionType,
  SolveABTestStatus,
} from 'src/services/agent-ab-test/types';
import { WorkflowVersionType } from 'src/slices/workflow-preview/types';
import { SolveWidgetProduct } from 'src/types/types';
import { ConversationChannel } from 'src/types/workflowBuilderAPITypes';

/** Response Bodies */
export type BaseInsightResponse = {
  donut?: DonutGraphDataType;
  line?: LineGraphDataType;
  misc?: Record<string, unknown>;
  stacked_bar?: StackedBarGraphDataType | null;
  type: GraphResponseType;
};
export interface ArticlePerformanceResponse extends BaseInsightResponse {
  misc: {
    article_surfaced: number;
    clicked_count: number;
    contained_resolutions: number;
    conversation_count: number;
    deflected_conversation_count: number;
    delta_value: number | null;
    sub_title: string;
    sub_title_conversation: string;
    sub_title_deflected_conversation: string;
    title: string;
    value?: number;
    verified_resolutions: number;
  };
}
export interface ArticleUserFeedbackResponse extends BaseInsightResponse {
  donut: DonutGraphDataType;
  misc: {
    delta_value?: number;
    negative: number;
    negative_label: string;
    not_answered: number;
    not_answered_label: string;
    positive: number;
    positive_label: string;
    sub_title?: string;
    title: string;
    value?: number;
  };
}
export interface CommonCsatResponse extends BaseInsightResponse {
  bar: BarGraphDataType;
  donut: DonutGraphDataType;
  misc: {
    delta_total_response?: number;
    delta_value?: number;
    sub_title?: string;
    title: string;
    total_response: number;
    value?: number;
  };
  stacked_bar?: null;
}

export interface PlaybookConversationsResponse extends BaseInsightResponse {
  misc: {
    title: string;
    total_conversations: number;
  };
}

export interface QuickFeedbackResponse extends BaseInsightResponse {
  bar: BarGraphDataType;
  donut: DonutGraphDataType;
  misc: {
    delta_total_response?: number;
    delta_value?: number;
    negative: number;
    negative_label?: string;
    not_answered: number;
    not_answered_label?: string;
    positive: number;
    positive_label?: string;
    title?: string;
  };
  stacked_bar?: null;
}

export interface HelpdeskCsatResponse extends BaseInsightResponse {
  bar?: BarGraphDataType;
  donut: DonutGraphDataType;
  misc: {
    bad: number;
    delta_value?: number;
    good: number;
    offered: number;
    title: string;
    total_conversations?: number;
    unoffered: number;
  };
  stacked_bar?: null;
}
export interface CommonTrendBarGraphResponse extends BaseInsightResponse {
  bar: BarGraphDataType;
  donut: DonutGraphDataType;
  misc: {
    delta_value?: number;
    sub_title?: string;
    title: string;
    total_response: number;
    value?: number;
  };
  stacked_bar?: null;
}
export type DonutGraphResponse =
  | CommonCsatResponse
  | ArticleUserFeedbackResponse
  | CommonTrendBarGraphResponse;

export interface ArticleCsatResponse extends BaseInsightResponse {
  bar: BarGraphDataType;
  misc: {
    delta_total_response?: number;
    delta_value?: number;
    sub_title: string;
    title?: string;
    total_response: number;
    type?: string | null;
    value: number;
  };
  stacked_bar?: null;
}
export interface OverviewChatTrendResponse extends BaseInsightResponse {
  bar?: null;
  misc: {
    title?: string;
  };
  stacked_bar: StackedBarGraphDataType;
}
export interface EmailNoResponseResponse extends BaseInsightResponse {
  bar: BarGraphDataType;
  misc: {
    delta_total_response: number;
    sub_title: string;
    title: string;
    total_response: number;
  };
}
export type BarGraphResponse = ArticleCsatResponse | OverviewChatTrendResponse;
export interface ArticleDetailResponse extends BaseInsightResponse {
  line: LineGraphDataType;
}
export interface OverviewOvertimeGraphResponse extends ArticleDetailResponse {
  line: OverviewOvertimeGraphDataType;
}
export interface TopicDetailResponse extends BaseInsightResponse {
  line: LineGraphDataType;
}
export type LineGraphResponse = ArticleDetailResponse;
export interface ConversationDeeplinkResponse {
  conversation_id: string;
  product_mode: ProductMode;
}
export interface ArticleTableResponse {
  data: InsightArticle[];
  metadata: BaseTableMetadata;
  nextPage?: boolean;
}
export interface EmailArticleTableResponse {
  data: InsightEmailArticle[];
  metadata: BaseTableMetadata;
  nextPage?: boolean;
}

export interface InsightSubtopic {
  cluster_id: string;
  cluster_name: string;
}
export interface ChatTableResponse {
  data: InsightConversation[];
  metadata: BaseTableMetadata;
  nextPage?: boolean;
}

export interface EmailChatTableResponse {
  data: InsightEmailConversation[];
  metadata: BaseTableMetadata;
  nextPage?: boolean;
}

export interface AssistChatTableResponse {
  data: InsightAssistConversation[];
  metadata: BaseTableMetadata;
  nextPage?: boolean;
}

export interface WorkflowTableResponse {
  data: InsightWorkflow[];
  metadata: BaseTableMetadata;
  nextPage?: boolean;
}
export interface AgentsTicketsTableResponse {
  data: InsightAgentsTicket[];
  metadata: BaseTableMetadata;
  nextPage?: boolean;
}

export interface AgentTicketsTableResponse {
  data: InsightAgentTicket[];
  metadata: BaseTableMetadata;
  nextPage?: boolean;
}

export type AuthorType = 'agatha' | 'agent' | 'unknown' | 'user' | 'automated';

type TranscriptEntryType =
  | 'user_reply'
  | 'playbook_conversation'
  | 'generated_reply';

type PlaybookTranscriptEntry =
  | UserReplyTranscriptEntry
  | PlaybookConversationTranscriptEntry
  | GeneratedReplyTranscriptEntry;

interface TranscriptEntryBase {
  timestamp: string;
  type: TranscriptEntryType;
}

interface UserReplyData {
  author_type: AuthorType;
  body: string;
  reply_id: string;
}

interface PlaybookConversationData {
  transcript_components: Array<TranscriptComponent>;
}

export interface UsageEvent {
  event_type:
    | 'add-generated-reply'
    | 'copy-clipboard-generated-reply'
    | 'copy-generated-reply';
  timestamp: string;
}

interface RelatedArticle {
  document_id: string;
  link: string;
  title: string;
}

interface GeneratedReplyData {
  related_articles: RelatedArticle[];
  suggestion: string;
  usage_events: UsageEvent[];
}

export interface UserReplyTranscriptEntry extends TranscriptEntryBase {
  data: UserReplyData;
  type: 'user_reply';
}

export interface PlaybookConversationTranscriptEntry extends TranscriptEntryBase {
  data: PlaybookConversationData;
  type: 'playbook_conversation';
}

export interface GeneratedReplyTranscriptEntry extends TranscriptEntryBase {
  data: GeneratedReplyData;
  type: 'generated_reply';
}

export interface PlaybookConversationDetailResponse {
  autonomous_agent_used?: boolean;
  context_variables?: Record<string, string>;
  conversation_id: string;
  modified_date?: string;
  playbook_transcript: Array<PlaybookTranscriptEntry>;
  quick_feedback_responses: Array<QuickFeedbackRecord>;
  sf_case_number: string | null;
  start_datetime?: string;
  time_spent_in_widget?: number;
  user_interactions?: number;
}

// How a proactive conversation was initiated.
export type ProactiveOrigin =
  | 'trigger_intent'
  | 'proactive_prompt'
  | 'open_widget'
  | 'outbound_call';

export interface ConversationDetailResponse {
  ab_test_samples?: ABTestSample[] | null;
  additional_feedback: string | null;
  ai_qa_scores?: AiQaScore[];
  autonomous_agent_used: boolean;
  conversation_id: string;
  // 'deflection' | 'proactive_conversation'. The backend always sends it
  // (defaulting to 'deflection'); optional here for fixture compatibility.
  conversation_type?: string;
  csat_feedback: string[];
  csat_resolve: boolean;
  csat_score: number;
  deflected: boolean;
  ending_sentiment_score: number | null;
  // Intent that initiated a proactive conversation, when known.
  initial_intent_id?: string | null;
  last_executed_intent_definition_id: string;
  last_executed_intent_title: string;
  last_executed_intent_user_query: string;
  modified_date: string;
  // Whether the user engaged with a proactive conversation; null for
  // deflection conversations.
  proactive_engaged?: boolean | null;
  // Timestamp of the user's first engagement with a proactive conversation.
  proactive_first_engaged_at?: string | null;
  proactive_insights?: Record<string, unknown> | null;
  proactive_origin?: ProactiveOrigin | null;
  // Trigger that started a proactive conversation, when applicable.
  proactive_trigger_id?: string | null;
  resolution_automated?: boolean | null;
  resolution_tier?: string | null;
  resolution_tier_insights?: ResolutionTierInsights | null;
  start_datetime: string;
  time_spent_in_widget: number;
  transcript: Array<string>;
}

export interface ResolutionTierInsights {
  customer_has_a_request?: boolean | null;
  customer_issue_solved?: boolean | null;
  customer_issue_solved_reasoning?: string | null;
  customer_request?: string | null;
  resolution_tier?: string | null;
  user_confirms_helpfulness?: boolean | null;
  user_leaves_chat_without_confirming_helpfulness?: boolean | null;
}

export interface TopicHeaderResponse {
  annual_save: number;
  avg_full_resolution_time_reduction: number;
  last_updated_at: string;
  ticket_value_reduction: number;
}
/** End Response Bodies */

/** Graph Types */
export type GraphLabelType =
  | 'Deflections'
  | 'Avg. CSAT'
  | 'Non-deflections'
  | 'Chats'
  // Conversations is what we convert from Chats type (backend)
  | 'Conversations'
  | '# of times knowledge article surfaced'
  | 'Deflected rate'
  | 'Knowledge article surfaced'
  | 'Contained resolutions'
  | 'Verified resolutions'
  | 'Resolution rate'
  | 'Quick feedback'
  | 'Relevance'
  | 'User engagement'
  // Ticket
  | 'Total received tickets'
  | 'Ticket assigned'
  | 'Solved ticket'
  | 'First contact resolution'
  | 'Full resolution time'
  | 'Time to first response'
  | 'Reopened tickets'
  | 'Satisfaction score'
  // Agent QA specific
  | 'Avg. first response time'
  | 'Avg. attention required time'
  | 'Avg. full resolution time';

export interface LineGraphUiOptions {
  color?: string;
  dashStyle?: DashStyleValue;
  linkedTo?: string;
  showInLegend?: boolean;
  topicId?: string;
  visible?: boolean;
  yAxis?: number;
}
export interface LineGraphValueData extends LineGraphUiOptions {
  extended_label?: string | null;
  extended_labels?: string[] | null;
  label: GraphLabelType;
  subValues?: LineGraphValueData[];
  // eslint-disable-next-line
  tooltip_values?: any;
  type?: string;
  value: (number | null)[];
}
export interface LineGraphDataType {
  values: LineGraphValueData[];
  x_axis: string[];
  x_axis_extended?: string[];
}
export interface OverviewOvertimeGraphValueData extends LineGraphValueData {
  category?: string | null;
  tooltip_values: {
    header?: string;
    label?: string;
    type: string;
    value: (number | null)[];
  }[];
}
export interface OverviewOvertimeGraphDataType extends LineGraphDataType {
  values: OverviewOvertimeGraphValueData[];
}
export type StackedBarGraphDataType = {
  categories: string[];
  values: {
    additional_values?: { category: string; value: number }[];
    category?: string;
    label: string;
    tooltip_header_label?: string;
    type?: string;
    value: number;
  }[];
};
export type BarGraphValueType = {
  additional_values?: { category: string; value: number }[];
  category?: string;
  extended_label?: string | null;
  hidden?: boolean;
  label: string;
  tooltip_header_label?: string;
  tooltip_values?: { label: string; type: string; value: number }[];
  type?: string | null;
  value: number;
  values?: BarGraphValueType[];
};
export type BarGraphDataType = {
  categories?: string[];
  values: BarGraphValueType[];
};
export type DonutGraphDataType = {
  values: {
    extended_label?: string | null;
    label: string;
    secondaryLabel?: string;
    type?: string | null;
    value: number;
  }[];
};
/** Eng Graph Types */

/** Request Bodies */
export interface TimestampRequest {
  end_timestamp: number;
  start_timestamp: number;
}

/**
 * Shared filter set accepted by every Solve Insights card endpoint.
 * Mirrors `InsightCardFilterParams` in forethought-server. Array values are
 * serialized as comma-joined strings on the wire, which the backend splits.
 */
export interface InsightCardFilterRequest {
  activate?: string;
  article_integration?: string | string[];
  autonomous_agent_used?: string;
  channels?: string | string[];
  chat?: string;
  conversation_id?: string;
  conversation_status?: string | string[];
  // Comma-separated subset of 'deflection', 'proactive_conversation'.
  conversation_types?: string | string[];
  csat_exists?: string;
  csat_ratings?: string | string[];
  cv_filters?: string;
  deflection?: string | string[];
  dropoff_ratings?: string | string[];
  ending_sentiments?: string | string[];
  intent_id?: string | string[];
  query_exists?: string;
  relevance_ratings?: string | string[];
  resolution_automated?: string | string[];
  resolution_tiers?: string | string[];
  rubric_filters?: string;
  tab?: string;
  topic_ids?: string | string[];
  types?: string | string[];
  user_query?: string;
  widget_ticket_id?: string;
  workflow_id?: string | string[];
  workflow_tags?: string | string[];
  workflow_versions?: string | string[];
}

export interface ArticleTopMetricRequest
  extends TimestampRequest, InsightCardFilterRequest {}

export interface ArticlePerformanceRequest
  extends TimestampRequest, InsightCardFilterRequest {
  product?: ProductMode;
  to_compare?: boolean;
}

export interface ChatsSavingsInsightsRequest
  extends TimestampRequest, InsightCardFilterRequest {
  event_filters?: string[] | string;
  is_overview?: boolean;
  product?: ProductMode;
  to_compare?: boolean;
  topics_only?: boolean;
}

export interface ProactiveConversationsRequest
  extends TimestampRequest, InsightCardFilterRequest {
  to_compare?: boolean;
}

export interface ProactiveFunnelRequest
  extends TimestampRequest, InsightCardFilterRequest {}

export interface EmailChatsRequest extends TimestampRequest2 {
  article_id?: string;
  conversation_id?: string;
  conversation_type?: string;
  deflection?: string;
  eligible?: string;
  email_sent?: string;
  feedback?: string;
  intent_id?: string;
  skip_reason?: string;
  source_id?: string;
  tab?: string;
  to_compare?: boolean;
  unanswered_cause?: string;
}

export interface OverallCSATRequest
  extends TimestampRequest, InsightCardFilterRequest {
  to_compare: boolean;
}

export interface TimestampRequest2 {
  agent_emails?: string[];
  end: number;
  start: number;
}

export interface ImpactInsightRequest {
  agent_emails?: string[];
  agent_group_ids?: string[];
  end: number;
  start: number;
}

export interface SentimentInsightRequest
  extends TimestampRequest2, InsightCardFilterRequest {
  overall?: boolean;
}

export interface AgentTicketMetadataValueOptionRequest extends TimestampRequest2 {
  metadata_key: string;
}

export interface WorkflowTopMetricRequest
  extends TimestampRequest2, InsightCardFilterRequest {}

export interface RelevanceAndEngagementInsightRequest
  extends TimestampRequest2, InsightCardFilterRequest {
  overall?: boolean;
  to_compare?: boolean;
}

export interface TopicDetailHeaderRequest {
  is_parent_topic?: boolean;
  scope: 'main' | 'detail';
  topic_id?: string;
}

export interface ConversationRequest {
  conversation_id: string;
}

export type ConversationReviewRating = 'missed_the_mark' | 'good_enough';

export interface ConversationReviewRequest {
  comment?: string | null;
  conversation_id: string;
  is_submitted: boolean;
  rating: ConversationReviewRating;
}

export interface ConversationReviewResponse {
  comment: string | null;
  conversation_id: string;
  created_at: string;
  is_submitted: boolean;
  last_edited_by: string;
  modified_date: string;
  rating: ConversationReviewRating;
  review_id: string;
}

export interface ReviewProgressRequest {
  channels?: string;
  end_timestamp: number;
  /**
   * When set, scopes the response to flagged conversations within this
   * workflow. The backend extension that honors this lives in
   * forethought-server PR 22154; until that merges, the param is silently
   * ignored and we get unscoped counts.
   */
  intent_workflow_id?: string;
  start_timestamp: number;
}

export interface ReviewProgressResponse {
  completed: number;
  pending: number;
}

export type InsightCardType =
  | 'unhappy topics'
  | 'cost-inefficient topics'
  | 'worst_performing_workflows';

export interface TopicCardRequest extends TimestampRequest2 {
  card_type: InsightCardType;
}

export interface QuickFeedbackInsightsRequest
  extends TimestampRequest, InsightCardFilterRequest {
  article_id?: string;
  event_filters?: string[] | string;
  is_overview?: boolean;
  product?: ProductMode;
  to_compare?: boolean;
}

interface BaseTableRequest {
  data_export_type: string;
  download?: boolean;
  end: number;
  page?: number;
  // sort_column and sort_direction is not required for topic table
  sort_column?: string;
  sort_direction?: InsightSortDirection;
  start: number;
}

export interface ArticleTableRequest extends BaseTableRequest {
  article_id?: string;
  channels: string | undefined;
  conversation_status?: string | string[];
  is_parent_topic?: boolean;
  sort_column: InsightArticleSortColumns;
  topic_id?: string;
  workflow_id?: string;
  workflow_versions?: string;
}

export interface EmailArticleTableRequest extends BaseTableRequest {
  article_id?: string;
  sort_column: InsightEmailArticleSortColumns;
}

// Human-feedback filter buckets for the chats table. Mutually exclusive over
// review-eligible conversations; mirrors the backend `HumanFeedbackFilter`
// enum (forethought-server). Sent to the chats-table endpoint as a
// comma-separated string via `human_feedback`. All three (or none) = no filter.
export const HUMAN_FEEDBACK_FILTER_VALUES = [
  'flagged',
  'feedback_given',
  'no_feedback',
] as const;
export type HumanFeedbackFilterValue =
  (typeof HUMAN_FEEDBACK_FILTER_VALUES)[number];

export interface ChatTableRequest extends BaseTableRequest {
  article_id?: string;
  chart_id?: string;
  chat: string;
  conversation_id: string;
  conversation_status?: string | string[];
  // Comma-separated conversation ids. When set, narrows the table to exactly
  // these conversations (the per-hunk source deep-link from a policy review).
  conversation_ids_subset?: string;
  // Comma-separated subset of 'deflection', 'proactive_conversation'.
  conversation_types?: string;
  cv_filters?: ContextVariableFilterItemValue[];
  dropoff_ratings?: boolean[];
  event_filters: EventChartProperty[];
  flagged_only?: boolean;
  // Comma-separated subset of HUMAN_FEEDBACK_FILTER_VALUES. Supersedes
  // `flagged_only` on the backend when present.
  human_feedback?: string;
  is_parent_topic?: boolean;
  // Overrides the default page size. Set small when only existence matters (a
  // deep-link gate) so the response isn't a full page of conversation rows.
  page_size?: number;
  // Comma-separated quick-feedback buckets: 'positive', 'negative', 'unanswered'.
  quick_feedback?: string;
  channel_mode?: string;
  // Whether the conversation has at least one quick-feedback comment.
  quick_feedback_comment_exists?: boolean;
  relevance_ratings?: string[];
  resolution_automated?: string[];
  resolution_tiers?: string[];
  sort_column: InsightConversationSortColumns;
  // A gepa_experiment id. When set, the chats table is narrowed server-side to
  // the conversations that produced that improved-policy recommendation.
  source_recommendation_id?: string;
  topic_id?: string;
  user_query: string;
  workflow_id?: string;
  workflow_versions?: string;
}

export interface EmailChatTableRequest extends BaseTableRequest {
  article_id?: string;
  chart_id?: string;
  conversation_id: string;
  cv_filters?: ContextVariableFilterItemValue[];
  dropoff_ratings?: boolean[];
  event_filters: EventChartProperty[];
  helpdesk_csat?: string;
  is_parent_topic?: boolean;
  relevance_ratings?: string[];
  resolution_automated?: string;
  resolution_tiers?: string;
  sort_column: InsightEmailConversationSortColumns;
  topic_id?: string;
  workflow_id?: string;
}

export type AssistChatTableRequest = BaseTableRequest &
  Omit<AssistBaseFilterRequest, 'end_timestamp' | 'start_timestamp'> & {
    article_id?: string;
    conversation_id?: string;
    cv_filters?: string;
    intent_id?: string;
  };

export interface AgentsTicketsTableRequest extends BaseTableRequest {
  agent_email?: string;
  agent_group_id?: string;
  agent_id?: string;
  agent_name?: string;
  sort_column: InsightAgentsTicketsSortColumns;
  ticket_channel?: string;
}

export interface AgentTicketsTableRequest extends BaseTableRequest {
  agent_email: string;
  agent_group_id?: string;
  agent_id?: string;
  agent_name?: string;
  // Comma-separated subset of 'evaluated', 'skipped', 'in_progress'.
  evaluation_status?: string;
  sort_column: InsightAgentTicketsSortColumns;
  ticket_channel?: string;
}

export interface AgentAiMetricCardsRequest extends TimestampRequest2 {
  agent_email?: string;
  agent_group_id?: string;
  agent_id?: string;
  agent_name?: string;
  // Comma-separated subset of 'evaluated', 'skipped', 'in_progress'.
  evaluation_status?: string;
  ticket_channel?: string;
}

export interface AgentAiMetricReasonsRequest extends TimestampRequest2 {
  model_id?: string;
}

export type TopicType = 'unresolved_first_contact' | 'top_topic';

export interface AgentTopicRequest extends TimestampRequest2 {
  agent_email: string;
  agent_group_id?: string;
  ticket_channel?: string;
  topic_type: TopicType;
}

export interface TopicTableRequest extends BaseTableRequest {
  channels: string | undefined;
  workflow_ids?: string[];
}

export interface WorkflowTableRequest extends BaseTableRequest {
  channels: string | undefined;
  conversation_status?: string | string[];
  sort_column: InsightWorkflowSortColumns;
  user_query: string;
}

export interface ArticleDetailHeaderRequest {
  article_id: string;
  channels: string | undefined;
  end: number;
  product?: ProductMode;
  start: number;
}

export interface ArticleContentRequest {
  article_id: string;
}

export interface ArticleLineGraphRequest {
  article_id: string;
  channels: string | undefined;
  end: number;
  period: InsightPeriod;
  product?: ProductMode;
  start: number;
}

export interface OverviewChatTrendGraphRequest {
  end: number;
  period: ChatTrendsPeriodicalFilter;
  start: number;
}

export interface OverviewOvertimeGraphRequest extends TimestampRequest2 {
  period: OverviewOvertimePeriod;
}

export interface TopicLineGraphRequest {
  end: number;
  is_parent_topic?: boolean;
  period: InsightPeriod;
  start: number;
  // UI
  subTopics: string[];
  topic_id: string;
}

/** End Request Bodies */

/** Request Metadatas */
interface BaseTableMetadata {
  end: number;
  next_page: number | null;
  page: number;
  page_size: number;
  sort_column: string;
  sort_direction: InsightSortDirection;
  start: number;
}
/** End Request Metadatas */

export interface InsightRelatedArticle {
  article_id: string;
  article_link: string;
  article_title: string;
}

export interface InsightRelatedIntent {
  intent_id: string;
  intent_name: string;
  workflow_id: string;
}

export interface InsightArticleContent {
  article_source_link: string;
  article_title: string;
  content: string;
  id: string;
  is_public: boolean;
}

export interface InsightArticle {
  article_integration: string | null;
  channels: string[];
  chats_count: number;
  chats_count_change?: number | null;
  click_rate: number;
  clicks: number;
  contained_resolutions?: number | null;
  content: string;
  csat: number;
  deflected_chats_count: number;
  deflected_chats_count_change?: number | null;
  deflected_rate: number;
  dropoff_breakdown: DropoffBreakdown;
  dropoff_rate: number;
  id: string;
  interactive_email_count: number;
  is_public: boolean;
  last_updated_at?: string | null;
  related_articles: InsightRelatedArticle[];
  relevance_breakdown: RelevanceBreakdown;
  source_link: string;
  times_surfaced: number;
  title: string;
  user_feedback_negative: number;
  user_feedback_positive: number;
  user_feedback_unanswered: number;
  verified_resolutions?: number | null;
  workflows: InsightRelatedIntent[];
}

export interface InsightEmailArticle {
  channels: string[];
  chats_count: number;
  content: string;
  deflected_count: number;
  id: string;
  interactive_email_count: number;
  related_articles: InsightRelatedArticle[];
  source_link: string;
  times_surfaced: number;
  title: string;
  user_feedback_negative: number;
  user_feedback_positive: number;
  user_feedback_unanswered: number;
  workflows: InsightRelatedIntent[];
}

export interface InsightWorkflowVariant {
  ab_test_config_id: string;
  ab_test_name: string;
  ab_test_status: SolveABTestStatus;
  avg_csat: number | null;
  chats: number | null;
  contained_resolutions?: number | null;
  deflected_count: number | null;
  dropoff_breakdown: DropoffBreakdown | null;
  end_condition_type: EndConditionType | null;
  end_date: string | null;
  engagement_rate: number | null;
  feedback_breakdown: FeedbackBreakdown | null;
  intent_workflow_version: number;
  is_control: boolean;
  non_deflected_count: number | null;
  potential_savings: number | null;
  realized_savings: number | null;
  relevance_breakdown: RelevanceBreakdown | null;
  sample_count: number | null;
  start_date: string | null;
  variant_name: string;
  verified_resolutions?: number | null;
  version_type: WorkflowVersionType;
}

export interface InsightWorkflow {
  ab_test_variants?: InsightWorkflowVariant[];
  avg_csat: number | null;
  channel: string;
  chats: number;
  chats_change?: number | null;
  contained_resolutions?: number | null;
  cost_per_ticket: number;
  deflected_count: number;
  deflected_count_change?: number | null;
  dropoff_breakdown: DropoffBreakdown;
  engagement_rate: number;
  feedback_breakdown: FeedbackBreakdown;
  intent_definition_id: string;
  intent_title: string | null;
  intent_workflow_id: string;
  is_active: boolean;
  is_autoflow: boolean;
  is_handoff: boolean;
  // is_loading is used for KR row loading state until KR endpoint hits
  is_loading?: boolean;
  non_deflected_count: number;
  non_deflected_count_change?: number | null;
  potential_savings: number;
  realized_savings: number;
  relevance_breakdown: RelevanceBreakdown;
  subRows?: InsightWorkflow[];
  surfaced_articles: InsightRelatedArticle[];
  /** Variant metadata when this row represents an AB test variant sub-row. */
  variantInfo?: ABTestVariantInfo;
  verified_resolutions?: number | null;
}

export interface InsightOverviewChatPerformance {
  CSAT: number;
  deflection_chats: number;
  deflection_rate: number;
  dropoff_breakdown: {
    dropoff_count: number;
    no_dropoff_count: number;
  };
  last_updated_at: string;
  realized_saving: number;
  relevance_breakdown: {
    irrelevant_count: number;
    relevant_count: number;
    somewhat_relevant_count: number;
  };
  total_chats: number;
}

export interface TopicsRelevanceResponse extends BaseInsightResponse {
  bar?: BarGraphDataType;
  donut: DonutGraphDataType;
  misc: {
    delta_value?: number;
    irrelevant: number;
    relevant: number;
    somewhat_relevant: number;
    title?: string;
  };
  stacked_bar?: null;
}

export interface TopicsEngagementResponse extends BaseInsightResponse {
  bar?: BarGraphDataType;
  donut: DonutGraphDataType;
  misc: {
    delta_value?: number;
    disengaged: number;
    engaged: number;
    title?: string;
  };
  stacked_bar?: null;
}

export interface AiQaScore {
  label?: string;
  normalized_score?: string;
  performance_goal: number | null;
  reasoning: string;
  rubric_name: string;
  score: string;
}

export interface ABTestSample {
  ab_test_config_id: string | null;
  ab_test_name: string | null;
  ab_test_run_id: string | null;
  ab_test_status: 'running' | 'completed' | 'canceled' | null;
  created_at: string | null;
  end_condition_type: 'fixed_duration' | 'sample_count' | null;
  end_date: string | null;
  intent_workflow_id: string | null;
  intent_workflow_version: number | null;
  is_control: boolean | null;
  product: string | null;
  sample_count: number | null;
  sample_id: string | null;
  start_date: string | null;
  variant_name: string | null;
  version_type: WorkflowVersionType | null;
}

export function toABTestVariantInfo(
  sample: ABTestSample,
): ABTestVariantInfo | null {
  if (
    !sample.ab_test_config_id ||
    !sample.ab_test_status ||
    !sample.variant_name ||
    !sample.version_type ||
    sample.is_control === null ||
    sample.intent_workflow_version === null
  ) {
    return null;
  }
  return {
    ab_test_config_id: sample.ab_test_config_id,
    ab_test_name: sample.ab_test_name ?? '',
    ab_test_status: sample.ab_test_status,
    end_condition_type: sample.end_condition_type ?? null,
    end_date: sample.end_date ?? null,
    intent_workflow_version: sample.intent_workflow_version,
    is_control: sample.is_control,
    sample_count: sample.sample_count ?? null,
    start_date: sample.start_date ?? null,
    variant_name: sample.variant_name,
    version_type: sample.version_type,
  };
}

export function buildVariantByWorkflowId(
  samples: ABTestSample[],
): Map<string, ABTestVariantInfo> {
  const map = new Map<string, ABTestVariantInfo>();
  for (const sample of samples) {
    if (!sample.intent_workflow_id) {
      continue;
    }
    const info = toABTestVariantInfo(sample);
    if (info) {
      map.set(sample.intent_workflow_id, info);
    }
  }
  return map;
}

export interface InsightConversation {
  ab_test_samples?: ABTestSample[] | null;
  ai_qa_scores?: AiQaScore[];
  articles: Array<InsightRelatedArticle>;
  autonomous_agent_used?: boolean;
  channel: string | null;
  chat: Array<string>;
  conversation_id: string;
  conversation_status?: 'in_progress' | 'finished';
  // 'deflection' | 'proactive_conversation'. The backend always sends it
  // (defaulting to 'deflection'); optional here for fixture compatibility.
  conversation_type?: string;
  csat: number;
  custom_events: {
    channel: ConversationChannel;
    event_configuration_id: string;
    event_configuration_name: string;
    event_properties: EventProperty;
    event_timestamp: string;
    intent_workflow_id: string | null;
    intent_workflow_version: number;
    product: SolveWidgetProduct;
  }[];
  detected_intents: Array<InsightRelatedIntent>;
  detected_topic: InsightSubtopic | null;
  detected_topics: InsightSubtopic[] | null;
  dropoff_rating?: boolean | null;
  dropoff_reasoning?: string | null;
  ending_sentiment_score?: number | null;
  // Intent that initiated a proactive conversation, when known.
  initial_intent_id?: string | null;
  // all the intents
  intent_count: number;
  // number of intents detected for this conversation
  is_deflected: boolean;
  is_flagged?: boolean;
  not_useful_count?: number;
  // Whether the user engaged with a proactive conversation; null for
  // deflection conversations.
  proactive_engaged?: boolean | null;
  // Trigger that started a proactive conversation, when applicable.
  proactive_trigger_id?: string | null;
  relevance_rating?: RelevanceRating | null;
  relevance_reasoning?: string | null;
  resolution_automated?: boolean | null;
  resolution_tier?: string | null;
  resolution_tier_insights?: ResolutionTierInsights | null;
  review_status?: ConversationReviewRating | null;
  time_spent_in_widget: number | null;
  timestamp: string;
  unanswered_count?: number;
  useful_count?: number;
  user_queries: string[];
  user_query: string;
  visitor_interacted: boolean | null;
  voice_call_duration: number | null;
  voice_from_number: string | null;
  voice_is_outbound: boolean | null;
  voice_is_web_call: boolean | null;
  voice_outbound_answered_by: 'user' | 'voicemail' | null;
  voice_outbound_engaged: boolean | null;
  voice_outbound_user_asked_followup: boolean | null;
  voice_outbound_workflow_id: string | null;
  voice_outbound_workflow_name: string | null;
  voice_sentiment: 'positive' | 'negative' | 'neutral' | null;
  voice_to_number: string | null;
  widget_ticket_id: string | null;
  workflow_tag: string | null;
}

export interface InsightEmailConversation {
  articles: Array<InsightRelatedArticle>;
  conversation_id: string;
  detected_intents: Array<InsightRelatedIntent>;
  email_workflow_execution_status: string;
  interactive_email_deflection_reason: string;
  is_autoflow: boolean;
  is_deflected: boolean;
  is_eligible: boolean;
  is_interactive_email: boolean;
  response_sent: boolean;
  resolution_automated?: boolean | null;
  resolution_tier?: string | null;
  resolution_tier_insights?: ResolutionTierInsights | null;
  skip_reason: string;
  ticket_body: string;
  ticket_id: string;
  ticket_title: string;
  timestamp: string;
  used_human_in_the_loop: boolean;
  user_feedback: string;
  zendesk_csat_comment: string | null;
  zendesk_csat_score: string | null;
}

export interface InsightAssistConversation {
  agent_email: string | null;
  articles: Array<InsightRelatedArticle>;
  autonomous_agent_used: boolean;
  context_variables?: Record<string, string>; // NEW
  conversation_id: string;
  detected_intents: Array<InsightRelatedIntent>;
  email_workflow_execution_status: string;
  generated_reply_count: number;
  intent_count: number;
  interactive_email_deflection_reason: string;
  is_autoflow: boolean;
  is_deflected: boolean;
  is_eligible: boolean;
  is_interactive_email: boolean;
  not_useful_count?: number;
  response_sent: boolean;
  sf_case_number: string | null;
  skip_reason: string;
  standardized_ticket_id: string;
  ticket_body: string | null;
  ticket_source_created_date: string;
  ticket_source_doc_type: string;
  ticket_source_id: string | null;
  ticket_source_link: string;
  ticket_title: string;
  time_spent_in_widget: number | null;
  timestamp: string;
  unanswered_count?: number;
  used_generated_reply: boolean;
  useful_count?: number;
  user_feedback: string;
  visitor_interacted: boolean;
}

export interface InsightAgentTicket {
  agent_name: string;
  avg_ai_score: number | null;
  body: string;
  cluster_id: string;
  cluster_name: string;
  date_created: string;
  first_contact_resolution: boolean | null;
  full_resolution_time_secs: number | null;
  is_ai_skipped?: boolean | null;
  metric_scores: Record<
    string,
    Record<string, string | number | undefined | null>
  >;
  num_replies: number;
  miaw_messaging_id?: string | null;
  source_id: string;
  source_link: string;
  ticket_agent_summary: string | null;
  ticket_channel: string;
  ticket_id: string;
  ticket_status: string;
  time_in_attention_required_secs: number | null;
  time_to_first_reply_secs: number | null;
  title: string;
}

export interface InsightAgentsTicket {
  agent_email: string;
  agent_group_id: string;
  agent_name: string;
  avg_ai_score: number | null;
  avg_full_resolution_time_secs: number | undefined | null;
  avg_time_in_attention_required_secs: number | null;
  avg_time_to_first_reply_secs: number | undefined | null;
  first_contact_resolution_count: number;
  first_contact_resolution_rate: number;
  helpdesk_agent_id: string;
  metric_scores: Record<
    string,
    Record<string, string | number | string[] | null>
  >;
  ticket_assigned_count: number;
  ticket_replied_count: number;
  ticket_solved_count: number;
  ticket_solved_rate: number;
}

export type MetricScores = {
  avg_score: number;
  model_id: string;
  model_name: string;
  performance_goal: number | null;
  reasons: string[];
  type: string;
};

export interface InsightAgentAiMetricCard {
  agent_email: string;
  agent_name: string;
  categories: string[];
  cluster_id: string | null;
  cluster_name: string | null;
  curr_metric_scores: Record<string, MetricScores>;
  prev_metric_scores: Record<string, MetricScores>;
  solved_tickets: number;
  total_tickets: number;
}

export interface InsightAgentAiMetricReasons {
  [key: string]: {
    config_type: string;
    model_definition: string;
    model_id: string;
    model_name: string;
    model_scoring_definition: string;
    solved_ticketreasonss: string[];
    summarized_reason: string;
  };
}

export interface InsightAgentReportSummary {
  data: string | null;
}

export interface InsightAgentTopicCard {
  cluster_id: string;
  cluster_name: string;
  value: number | null;
}

export interface InsightsChatsSavingMisc {
  [key: string]: unknown;
  chats: number;
  contained_resolutions?: number | null;
  cost_per_ticket: number;
  date_range: string;
  deflections: number;
  deflections_delta_percentage?: number;
  non_deflections: number;
  non_deflections_delta_percentage?: number;
  potential_savings: number;
  previous?: InsightsChatsSavingMisc;
  realized_savings: number;
  verified_resolutions?: number | null;
}

export type AiQaConfigType = 'agent' | 'user' | 'settings';

export interface AiQaConfig {
  apply_to_filters?: MetadataFilterItem[][];
  config_type: AiQaConfigType;
  created_by: string;
  created_date: string;
  deleted_at: string;
  deleted_by: string;
  exclude_score: boolean | null;
  is_active: boolean;
  is_deleted: boolean;
  last_updated_at: string;
  last_updated_by: string;
  model_definition: string;
  model_id: string;
  model_name: string;
  model_scoring_definition: string;
  org_id: string;
  performance_goal?: {
    is_active?: boolean | null;
    value?: number | null;
  };
}

export interface AiQaConfigResponse {
  data: AiQaConfig[];
}

export interface InsightChatsSavingsResponseWithComparison extends BaseInsightResponse {
  misc: InsightsChatsSavingMisc;
}

export interface ProactiveConversationsMisc {
  // Required for compatibility with BaseInsightResponse['misc']
  // (Record<string, unknown>), mirroring InsightsChatsSavingMisc.
  [key: string]: unknown;
  date_range: string | null;
  // Fractional change vs. the previous period; null when there is no
  // comparison data.
  delta_percentage: number | null;
  engaged_count: number;
  not_engaged_count: number;
  previous?: ProactiveConversationsMisc;
  // Total conversations classified as proactive (engaged + not engaged).
  proactive_conversations: number;
}

export interface ProactiveConversationsResponse extends BaseInsightResponse {
  misc: ProactiveConversationsMisc;
  type: 'insights_proactive_conversations';
}

export interface ProactiveFunnelResponse extends BaseInsightResponse {
  misc: {
    engaged_conversations: number;
    // null when impressions is 0.
    engagement_rate: number | null;
    impressions: number;
  };
  type: 'insights_proactive_funnel';
}

export interface InsightChatsSavingsResponse extends BaseInsightResponse {
  misc: {
    chats: number;
    cost_per_ticket: number;
    deflections: number;
    non_deflections: number;
    potential_savings: number;
    realized_savings: number;
  };
  type: 'insights_chats_savings';
}

export interface InsightAgentsHeaderResponse {
  ai_metric_scores: Record<
    string,
    Record<string, string | number | undefined | null>
  >;
  ending_sentiment_percentage: number;
  first_contact_resolution_count: number;
  first_contact_resolution_percentage: number;
  full_resolution_time_secs: number;
  satisfaction_score_percentage: number;
  solved_ticket_count: number;
  solved_ticket_percentage: number;
  ticket_assigned_count: number;
  time_in_attention_required_secs: number | null;
  time_to_first_response: number;
  total_ticket_count: number;
}

export interface InsightAgentsHeaderComparisonResponse {
  current: InsightAgentsHeaderResponse;
  previous: InsightAgentsHeaderResponse;
}

export interface ABTestVariantInfo {
  ab_test_config_id: string;
  ab_test_name: string;
  ab_test_status: SolveABTestStatus;
  end_condition_type: EndConditionType | null;
  end_date: string | null;
  intent_workflow_version: number;
  is_control: boolean;
  sample_count: number | null;
  start_date: string | null;
  variant_name: string;
  version_type: WorkflowVersionType;
}

export interface FilterOptionsResponse {
  data: {
    ab_test_variants?: ABTestVariantInfo[];
    label: string;
    secondary_value?: string;
    type?: string;
    value: string;
  }[];
}

export interface AssistOverviewFilterOptionsResponse {
  agents: {
    label: string;
    value: string;
  }[];
  agent_groups: {
    label: string;
    value: string;
  }[];
  dashboard_agents: {
    label: string;
    value: string;
  }[];
  non_dashboard_agents: {
    label: string;
    value: string;
  }[];
}

export type InsightArticleSortColumns =
  | 'title'
  | 'click_rate'
  | 'times_surfaced'
  | 'user_feedback_positive'
  | 'quick_feedback';

export type InsightEmailArticleSortColumns =
  | 'chats_count'
  | 'deflected_count'
  | 'times_surfaced'
  | 'user_feedback';

export type InsightConversationSortColumns =
  | 'timestamp'
  | 'user_query'
  | 'quick_feedback'
  | 'csat'
  | 'channel';

export type InsightEmailConversationSortColumns =
  | 'timestamp'
  | 'source_id'
  | 'is_deflected'
  | 'is_eligible'
  | 'email_sent'
  | 'user_feedback';

export type GraphResponseType =
  | 'insights_articles_performance'
  | 'insights_feedback_articles'
  | 'insights_csat_topic'
  | 'insights_csat_articles'
  | 'insights_article_detail'
  | 'insights_csat_topic'
  | 'insights_topic_detail'
  | 'insights_chats_savings'
  | 'insights_csat_common'
  | 'insights_overview'
  | 'insights_overall_relevance'
  | 'insights_quick_feedback'
  | 'insights_feedback'
  | 'insights_helpdesk_csat'
  | 'overview_ka_bar_chart_insight'
  | 'overview_csat_bar_chart_insight'
  | 'overview_deflected_rate_bar_chart_insight'
  | 'overview_deflected_count_bar_chart_insight'
  | 'insights_overall_engagement'
  | 'overview_quick_feedback_bar_chart_insight'
  | 'overview_resolution_rate_bar_chart_insight'
  | 'overview_relevance_bar_chart_insight'
  | 'overview_engagement_bar_chart_insight'
  | 'insights_workflow_csat'
  | 'insights_overview_overtime_chart'
  | 'insights_workflow_detail_chart'
  | 'email_no_response_insight'
  | 'insights_agents_ticket_performance_chart'
  | 'insights_agents_ai_metric_performance_chart'
  | 'insights_proactive_conversations'
  | 'insights_proactive_funnel';

export interface SubtopicInformation {
  cluster_id: string;
  cluster_name: string;
  cluster_tier: ClusterTier | null;
}

export interface TopicInformationResponse {
  chats_count: number;
  cluster_id: string;
  cluster_name: string;
  cost_per_ticket: number;
  csat: number;
  deflected_chats_count: number;
  ka_surfaced_count: number;
  non_deflected_chats_count: number;
  potential_savings: number;
  realized_savings: number;
  subtopics: Array<SubtopicInformation>;
}

export interface AiQaSettingResponse {
  color_thresholds: Record<string, number>;
  created_by: string;
  created_date: string;
  last_updated_at: string | null;
  last_updated_by: string | null;
  rubric_weights: Record<string, number>;
  setting_id: string;
  sort_order: Record<string, string[]>;
  unified_sort_order: string[];
  weighted_scoring_enabled: boolean;
}

export interface AgentQaScorecardUpdateRequest {
  color_thresholds?: Record<string, number>;
  rubric_weights?: Record<string, number>;
  weighted_scoring_enabled?: boolean;
}

export interface TopicInformationRequest {
  end: number;
  is_parent_topic?: boolean;
  start: number;
  topic_id: string;
}

export interface AgentsTicketRequest extends TimestampRequest2 {
  agent_email?: string;
  agent_group_id?: string;
  channels?: string;
  // Comma-separated subset of 'evaluated', 'skipped', 'in_progress'.
  evaluation_status?: string;
  first_contact_resolution?: string;
  ticket_channel?: string;
  ticket_status?: string;
  topic_id?: string;
}

export interface AgentQaSummaryRequest extends TimestampRequest2 {
  agent_email?: string;
  agent_group_id?: string;
  ticket_channel?: string;
}

export interface AgentsTicketComparisonRequest extends AgentsTicketRequest {
  graph_type: GraphDataType;
  period: OverviewOvertimePeriod;
}

export interface AiQaConfigurationsRequest {
  config_type: AiQaConfigType;
}

export interface AiQaInsightCardsRequest {
  article_id?: string;
  channels?: string;
  chart_id?: string;
  chat?: string;
  conversation_id?: string;
  cv_filters?: ContextVariableFilterItemValue[];
  end: number;
  event_filters?: EventChartProperty[];
  is_parent_topic?: boolean;
  start: number;
  topic_id?: string;
  user_query?: string;
  widget_ticket_id?: string;
  workflow_id?: string;
}

export interface AiQaConfigurationsV2Request {
  config_type: AiQaConfigType;
  sort_order: string[];
}

export interface AgentQaConfigurationRankRequest {
  unified_sort_order: string[];
}

export interface AiQaSettingsColorThresholdRequest {
  color_thresholds: Record<string, number>;
}

export interface AiQaConfigurationPayloadRequest {
  apply_to_filters?: MetadataFilterItem[][];
  config_type: AiQaConfigType;
  exclude_score: boolean;
  is_active: boolean;
  model_definition: string;
  model_id?: string;
  model_name: string;
  model_scoring_definition: string;
  performance_goal?: {
    is_active?: boolean | null;
    value?: number | null;
  };
}

export interface AiQaConfigurationTestPayloadRequest {
  apply_to_filters?: MetadataFilterItem[][] | null;
  config_type: AiQaConfigType;
  model_definition?: string;
  model_name?: string;
  model_scoring_definition?: string;
  source_id: string;
}

export interface AiQaConfigurationTestRubricResult {
  justification: string;
  rating: string | number;
  rating_index?: number;
  rubric_name: string;
}

export interface AiQaConfigurationTestResponse {
  data: AiQaConfigurationTestRubricResult[];
}

export interface AgentQaConfigurationTestResult {
  agent_name: string;
  model_name: string;
  reasoning: string;
  score: string;
}

export interface AgentQaConfigurationTestResponse {
  data: AgentQaConfigurationTestResult[];
}

export interface InsightTopicCard {
  cluster_id: string;
  cluster_name: string;
  csat: number | null;
  deflected_chats_count: number;
  deflected_rate: number;
  non_deflected_chats_count: number;
  non_deflected_rate: number;
  potential_saving: number;
  top_surfaced_article: InsightRelatedArticle;
}

export interface TopicCardInsightResponse {
  top_topics: InsightTopicCard[];
}

export interface InsightWorstWorkflowCard {
  avg_csat: number | null;
  intent_definition_id: string;
  intent_title: string | null;
  intent_workflow_id: string;
  is_autoflow: boolean;
  non_deflected_count: number;
  non_deflected_rate: number;
  potential_saving: number;
}

export interface TopicCardWorstWorkflowResponse {
  worst_workflows: InsightWorstWorkflowCard[];
}

export interface GeneratedArticleRequest {
  is_parent_topic?: boolean;
  topic_id?: string;
}

export interface WorkflowDetailHeaderRequest extends TimestampRequest2 {
  workflow_id: string;
}

export interface WorkflowDetailChartRequest extends TimestampRequest2 {
  period: InsightPeriod;
  workflow_id: string;
}

export interface InsightWorkflowDetail {
  avg_csat: number;
  channel: string;
  chats: number;
  contained_resolutions?: number | null;
  cost_per_ticket: number;
  deflected_count: number;
  dropoff_breakdown: DropoffBreakdown;
  engagement_rate: number;
  feedback_breakdown: FeedbackBreakdown;
  intent_definition_id: string;
  intent_title: string;
  intent_workflow_id: string;
  is_autoflow: boolean;
  non_deflected_count: number;
  potential_savings: number;
  realized_savings: number;
  relevance_breakdown: RelevanceBreakdown;
  surfaced_articles: InsightRelatedArticle[];
  surfaced_articles_count: number;
  verified_resolutions?: number | null;
}

export interface WorkflowDetailGraphValueData extends LineGraphValueData {
  category: string | null;
  tooltip_values: {
    header?: string;
    label?: string;
    type: string;
    value: number[];
  }[];
}

export interface WorkflowDetailGraphDataType extends LineGraphDataType {
  values: WorkflowDetailGraphValueData[];
}

export interface InsightWorkflowDetailChart extends ArticleDetailResponse {
  line: WorkflowDetailGraphDataType;
}

export interface AssistBaseFilterRequest {
  agent_email?: string;
  agent_email_set: 'yes' | 'no' | null;
  end: number;
  generated_reply: 'used' | 'not_used' | null;
  interacted: 'yes' | 'no' | null;
  source_id: string | undefined;
  start: number;
  workflow_id: string | null;
}

export interface OrgFeatureQaConfigResponse {
  apply_to_filters_feature: boolean;
  enabled: boolean;
  max_ai_qa_models_allowed: number;
}

export interface SentimentMetrics {
  negative_count: number;
  neutral_count: number;
  positive_count: number;
}

export type AgentAccessType = 'available' | 'not_available' | 'restricted';

export interface AssistFunnelMetrics {
  assist_coverage_rate: number;
  engagement_rate: number;
  helpful_rate: number;
  tickets_engaged: number;
  tickets_helpful: number;
  tickets_not_engaged: number;
  tickets_not_helpful: number;
  tickets_not_visible: number;
  tickets_opened: number;
  tickets_visible: number;
  total_tickets: number;
}

export interface AssistOverviewResponse {
  data: {
    funnel_metrics: AssistFunnelMetrics;
  };
  metadata: {
    end: number;
    last_updated_at: string;
    start: number;
  };
}

export interface AssistOverviewRequest {
  agent_emails?: string[];
  agent_group_ids?: string[];
  end: number;
  interaction_type?: 'all' | 'visible' | 'engaged' | 'helpful';
  start: number;
}

export interface InsightActionRecord {
  classification: string;
  display_name: string;
  event_type: string;
  volume: number;
}

export interface ActionRecordsResponse {
  data: InsightActionRecord[];
}

export interface ActionRecordsRequest {
  agent_emails?: string[];
  agent_group_ids?: string[];
  end_timestamp: number;
  engagement_level?: 'visible' | 'engaged' | 'helpful';
  start_timestamp: number;
}

export interface EventConfigResponse {
  engaged_events: string[];
  helpful_events: string[];
}

export interface UpdateEventConfigRequest {
  engaged_events: string[];
  helpful_events: string[];
}

export interface AvailableEvent {
  display_name: string;
  event_type: string;
}

export interface AssistEventGroupByDimension {
  display_name: string;
  field: string;
}

export interface AssistEventCardOptionsResponse {
  events: AvailableEvent[];
  group_by_dimensions: AssistEventGroupByDimension[];
}

export type AssistOverviewAgentsSortColumn =
  | 'agent_name'
  | 'tickets_assigned'
  | 'assist_engaged'
  | 'assist_helpful'
  | 'first_contact_resolution'
  | 'avg_resolution_time'
  | 'avg_first_response_time'
  | 'avg_replies_per_ticket';

export interface AssistOverviewAgentsRequest {
  agent_emails?: string[];
  agent_group_ids?: string[];
  end_timestamp: number;
  interaction_type?: 'all' | 'engaged' | 'helpful' | 'visible';
  page?: number;
  page_size?: number;
  search_query?: string;
  sort_column?: AssistOverviewAgentsSortColumn;
  sort_direction?: 'ASC' | 'DESC';
  start_timestamp: number;
}

export interface AssistOverviewAgentItem {
  agent_email: string | null;
  agent_name: string;
  assist_engaged: number;
  assist_helpful: number;
  avg_first_response_time: number | null;
  avg_replies_per_ticket: number | null;
  avg_resolution_time: number | null;
  first_contact_resolution: number;
  tickets_assigned: number;
}

export interface AssistOverviewAgentsResponse {
  data: AssistOverviewAgentItem[];
  metadata: Record<string, unknown>;
}

export interface AssistOverviewArticlesRequest {
  end_timestamp: number;
  page?: number;
  page_size?: number;
  search_query?: string;
  sort_column?: 'article_title' | 'tickets_returned';
  sort_direction?: 'ASC' | 'DESC';
  start_timestamp: number;
}

export interface AssistOverviewArticleItem {
  article_id: string;
  article_title: string;
  tickets_returned: number;
}

export interface AssistOverviewArticlesMetadata {
  [key: string]: unknown;
}

export interface AssistOverviewArticlesResponse {
  data: AssistOverviewArticleItem[];
  metadata: AssistOverviewArticlesMetadata;
}

// Impact comparison chart types
export interface MetricSegment {
  count: number;
  direction: 'improvement' | 'neutral' | 'regression' | null;
  label: string;
  percent_change: number | null;
  value: number;
  visible_direction: 'improvement' | 'neutral' | 'regression' | null;
  visible_percent_change: number | null;
}

export type DenominatorMode = 'eligible' | 'visible';

export interface SingleMetricImpactData {
  metric: string;
  segments: MetricSegment[];
  unit: string;
}

export interface ImpactMetadata {
  end: number;
  last_updated_at: string;
  previous_end: number | null;
  previous_start: number | null;
  start: number;
}

export interface SingleMetricImpactResponse {
  data: SingleMetricImpactData;
  metadata: ImpactMetadata;
}

/** Mirrors backend TicketTableSortColumn enum */
export const TicketTableSortColumn = {
  NUMBER_OF_REPLIES: 'reply_count',
  RESOLUTION_TIME: 'resolution_time_sec',
  SOURCE_ID: 'source_id',
  TIME_TO_FIRST_REPLY: 'first_response_time_sec',
  TIMESTAMP: 'ticket_created_date',
} as const;

export type TicketTableSortColumn =
  (typeof TicketTableSortColumn)[keyof typeof TicketTableSortColumn];

export interface TicketTableRequest {
  agent_emails?: string[];
  agent_group_ids?: string[];
  end: number;
  interaction_type?: 'all' | 'engaged' | 'helpful' | 'visible';
  page: number;
  page_size: number;
  sort_column: TicketTableSortColumn;
  sort_direction: 'ASC' | 'DESC';
  start: number;
  ticket_id_filter?: string;
  ticket_title_filter?: string;
}

export interface TicketTableRow {
  agent_names: string | null;
  assist_engaged: boolean;
  assist_helpful: boolean;
  feed_helpful: boolean;
  feed_used: boolean;
  note_used: boolean;
  number_of_replies: number | null;
  reply_generation_helpful: boolean;
  resolution_time: number | null;
  search_helpful: boolean;
  search_used: boolean;
  sf_case_number: string | null;
  source_id: string;
  ticket_id: string;
  ticket_status: string | null;
  ticket_title: string | null;
  time_to_first_reply: number | null;
  timestamp: string;
}

export interface TicketTableMetadata {
  end: number;
  next_page: number | null;
  page: number;
  page_size: number;
  sort_column: TicketTableSortColumn;
  sort_direction: 'ASC' | 'DESC';
  start: number;
}

export interface TicketTableResponse {
  data: TicketTableRow[];
  metadata: TicketTableMetadata;
}

export interface AgentStatsRequest {
  agent_email: string;
  end: number;
  start: number;
}

export interface AgentInfoRequest {
  agent_email: string;
}

export interface AgentStatsData {
  assist_engaged: number;
  assist_helpful: number;
  assist_utilization: number;
  avg_first_response_time: number | null;
  avg_first_response_time_helpful: number | null;
  avg_resolution_time: number | null;
  avg_resolution_time_helpful: number | null;
  first_contact_resolution: number;
  first_contact_resolution_helpful: number;
  solved_tickets: number;
  tickets_assigned: number;
  tickets_replied: number;
  tickets_replied_helpful: number;
}

export interface AgentStatsResponse {
  comparison_data: AgentStatsData | null;
  comparison_metadata: ComparisonMetadata | null;
  data: AgentStatsData;
  metadata: Record<string, unknown>;
}

// Agent detail time series types
export type AgentTimeSeriesGranularity = 'day' | 'week' | 'month';

export interface AgentTrendsRequest {
  agent_email: string;
  end_timestamp: number;
  granularity?: AgentTimeSeriesGranularity;
  start_timestamp: number;
  to_compare?: boolean;
}

export interface EngagementTrendDataPoint {
  engaged_tickets: number;
  helpful_tickets: number;
  period_start: string;
}

export interface ComparisonMetadata {
  end: number;
  start: number;
}

export interface EngagementTrendsResponse {
  comparison_data: EngagementTrendDataPoint[] | null;
  comparison_metadata: ComparisonMetadata | null;
  data: EngagementTrendDataPoint[];
}

export interface OutcomeTrendDataPoint {
  avg_first_response_time: number | null;
  avg_resolution_time: number | null;
  closed_helpful_count: number;
  fcr_percent: number | null;
  helpful_ticket_count: number;
  period_start: string;
}

export interface OutcomeTrendsResponse {
  comparison_data: OutcomeTrendDataPoint[] | null;
  comparison_metadata: ComparisonMetadata | null;
  data: OutcomeTrendDataPoint[];
}

export interface AgentInfoResponse {
  agent_name: string;
  agent_email: string;
}

// Scheduled Outbound Calls
export interface ScheduledOutboundCallItem {
  bulk_id: string | null;
  call_id: string;
  context: Record<string, unknown>;
  conversation_id: string | null;
  created_at: string | null;
  from_number: string;
  initial_workflow_id: string;
  scheduled_time: string;
  status: string;
  to_number: string;
}

export interface ScheduledOutboundCallsTableRequest {
  bulk_id?: string;
  end?: number;
  has_bulk_id?: boolean;
  page?: number;
  page_size?: number;
  search_field?: string;
  search_query?: string;
  sort_column?: string;
  sort_direction?: InsightSortDirection;
  start?: number;
  statuses?: string[];
}

export interface ScheduledOutboundCallsMetadata {
  next_page: number | null;
  page: number;
  page_size: number;
  sort_direction: InsightSortDirection;
}

export interface ScheduledOutboundCallsTableResponse {
  data: ScheduledOutboundCallItem[];
  metadata: ScheduledOutboundCallsMetadata;
  nextPage?: boolean;
}
